import { monthFirst } from './round-config'
import { el } from './ui'

// The time wheel (Liam, 26 Oct 2026, on "Finished a while ago?": "this would be better if you could tap and then pick and
// change time the flow isnt very good atm", then design B, https://claude.ai/artifact/NinRbbtA3w5jNDDiRNTxYU): the hours,
// minutes and am/pm showing at once, already on Roundly's guess, so one scroll and one button finish it, with no box to
// tap first. It drives the chooser's own time box (kept, out of sight until a keyboard reaches it), which stays the one
// value the chooser reads and the control a screen reader uses; the wheel itself is for the finger.

// Each row 44 high; 88 of space above and below, so row i sits in the middle band at scrollTop i × 44 (snapped by its
// centre: styles.css).
const ROW = 44

interface Col { list: HTMLElement; values: string[]; labels: string[] }

export interface TimeWheel { el: HTMLElement; commit: () => void }

export function timeWheel(input: HTMLInputElement): TimeWheel {
  // Every round reads a 12-hour clock ("2:30pm", a US round "2:30 PM"), so the wheel does too.
  const hours = ['12', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11']
  const halves = monthFirst() ? ['AM', 'PM'] : ['am', 'pm']
  const minutes = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
  const root = el('div', { class: 'time-wheel', 'aria-hidden': 'true' })
  const band = el('div', { class: 'time-wheel-band' })
  root.append(band)
  const col = (values: string[], labels: string[], name: string): Col => {
    const list = el('div', { class: `time-wheel-col time-wheel-${name}` })
    list.append(el('div', { class: 'time-wheel-pad' }))
    for (const [i, label] of labels.entries()) {
      const item = el('button', { type: 'button', class: 'time-wheel-item', tabindex: '-1' }, label)
      // A tap on a number brings it to the middle; the scroll that follows sets the time.
      item.addEventListener('click', () => { if (typeof list.scrollTo === 'function') list.scrollTo({ top: i * ROW, behavior: 'smooth' }); else { list.scrollTop = i * ROW; settle() } })
      list.append(item)
    }
    list.append(el('div', { class: 'time-wheel-pad' }))
    root.append(list)
    return { list, values, labels }
  }
  const h = col(hours, hours, 'hour')
  const m = col(minutes, minutes, 'minute')
  const ap = col(['am', 'pm'], halves, 'ampm')
  const cols = [h, m, ap]
  const at = (c: Col): number => Math.max(0, Math.min(c.values.length - 1, Math.round(c.list.scrollTop / ROW)))
  const mark = (c: Col): void => {
    const i = at(c)
    c.list.querySelectorAll('.time-wheel-item').forEach((b, j) => b.classList.toggle('is-on', j === i))
  }
  // The wheel's place as the box's "HH:MM", on a 24-hour clock.
  const read = (): string => {
    const hour = at(h) + (at(ap) === 1 ? 12 : 0)
    return `${String(hour).padStart(2, '0')}:${m.values[at(m)]}`
  }
  let quiet = false
  // Nothing is written to the box until the wheel has been put on its time or turned: a wheel not yet on the page reads 12am.
  let live = false
  const settle = (): void => {
    for (const c of cols) mark(c)
    if (quiet || !live) return
    const v = read()
    if (input.value === v) return
    input.value = v
    input.dispatchEvent(new Event('input', { bubbles: true }))
  }
  let timer: ReturnType<typeof setTimeout> | undefined
  for (const c of cols) {
    c.list.addEventListener('scroll', () => { if (!quiet) live = true; for (const x of cols) mark(x); clearTimeout(timer); timer = setTimeout(settle, 120) }, { passive: true })
  }
  // The box's time onto the wheel: when it opens, and when the box is changed by keyboard or "Just now".
  const show = (): void => {
    const t = /^(\d{1,2}):(\d{2})$/.exec(input.value)
    if (!t) return
    const hour = Number(t[1])
    quiet = true
    h.list.scrollTop = (hour % 12) * ROW
    m.list.scrollTop = Number(t[2]) * ROW
    ap.list.scrollTop = (hour >= 12 ? 1 : 0) * ROW
    for (const c of cols) mark(c)
    quiet = false
    live = true
  }
  input.addEventListener('change', show)
  // Placed once it is on the page (a list not yet shown cannot be scrolled).
  let tries = 0
  const place = (): void => {
    if (root.isConnected && h.list.clientHeight > 0) { show(); return }
    if (++tries < 30 && typeof requestAnimationFrame === 'function') requestAnimationFrame(place)
  }
  if (typeof requestAnimationFrame === 'function') requestAnimationFrame(place)
  // Save reads the box: the place the wheel is at now is put in it first, never the one before a turn still settling (Sol).
  return { el: root, commit: () => { clearTimeout(timer); settle() } }
}
